import { useEffect, useState } from 'react';

import { ActionResponse, ActionStatusHelper, AppEnumsRecord, appStateActions, sessionStateActions } from '@/Shared';
import { AppService, DEFAULT_LANG, getFingerprint, i18n, ResponseHook, Translation } from '@/Shared';
import { AuthService, ChallengeHelper, UserSessionModel } from '@/Domains';
import {  useAppDispatch, useAppSelector, useUser } from '@/OneLiners';

/**
 * =====================================================
 * useAppInitializer
 * =====================================================
 *
 * Centralized application bootstrap hook.
 *
 * Responsibilities:
 * - Initialize translations (offline/online aware)
 * - Create and validate visitor challenge
 * - Check and restore authenticated user session
 * - Load application enums
 * - Synchronize Redux state only when data changes
 * - Signal when the application is ready to render
 *
 * This hook runs once at application startup and ensures
 * a consistent initialization flow with minimal re-renders.
 *
 * Author: Samano CASTRE
 * Date: July 16, 2025
 */
export function useAppInitializer() {

    /**
     * =====================================================
     * Dependencies & Redux state
     * =====================================================
     */
    useUser();
    const dispatch = useAppDispatch();
    const { translation, enums } = useAppSelector(state => state.appState);
    const { userSession, userChallenge, } = useAppSelector(state => state.sessionState);
    
    const [initResponse, setInitResponse] = useState<ActionResponse>({status: 'loading'});

    /**
     * =====================================================
     * Translation coherence helper
     * =====================================================
     *
     * Ensures translation mode matches authentication state:
     * - Online translation for authenticated users
     * - Offline translation for visitors
     */
    const isTranslationCoherent = (session: UserSessionModel, translation: Translation) => {
        return (session && translation?.status === 'online') || (!session && translation?.status === 'offline');
    }

    /**
     * =====================================================
     * Effect: application bootstrap
     * =====================================================
     *
     * Orchestrates the full initialization sequence:
     * - Translation loading
     * - Visitor challenge creation
     * - Session validation
     * - Enums and session restoration
     *
     * Executes once on mount.
     */
    useEffect(() => {
        const bootstrap = async () => {

            try {

                let newTranslation = await initTranslation(!!userSession?.id);
                let newUserChallenge =  await createVisitor();
                let newEnums: AppEnumsRecord | null = null;
                let newSession: UserSessionModel | null = null;

                if (!newUserChallenge || !ChallengeHelper.isValidated(newUserChallenge)) {
                    ResponseHook.success(setInitResponse);
                }
                else {
                    const sessionChecked = await checkSession();
                    if (sessionChecked.authenticated) {
                        newEnums = await initEnums();
                        newSession = await initSession();
                        
                        if (!isTranslationCoherent(newSession, newTranslation)) {
                            newTranslation = await initTranslation(true);
                        }
                    }
                }

                if (newEnums && newEnums !== enums) {
                    dispatch(appStateActions.setEnums(newEnums));
                }

                if (newTranslation && translation !== newTranslation) {
                    dispatch(appStateActions.setTranslation(newTranslation));
                }

                if (newUserChallenge && newUserChallenge !== userChallenge) {
                    dispatch(sessionStateActions.setUserChallenge(newUserChallenge));
                }

                if (newSession && userSession !== newSession) {
                    dispatch(sessionStateActions.setUserSession(newSession));
                }
                setTimeout(()=>ResponseHook.success(setInitResponse), 50);
            }
            catch (error) {
                ResponseHook.error(setInitResponse, error, "Erreur lors de l'initialisation de l'app.");
            }
        }
        if(!userSession) {
            bootstrap();
        }
    }, [dispatch]);

     /**
     * =====================================================
     * Effect: app readiness flag
     * =====================================================
     *
     * Signals when the application is fully initialized
     * and safe to render.
     */
    useEffect(() => {
        if(ActionStatusHelper.isSucceeded(initResponse.status)) {
            dispatch(appStateActions.setAppIsReady(true));
        }
    },[initResponse.status, dispatch]);


     /**
     * =====================================================
     * initEnums
     * =====================================================
     *
     * Loads application-wide enums from the backend.
     */
    async function initEnums() {
        try {
            const data = await AppService.fetchEnums();
            const enums = data.data;
            return enums;
        }
        catch (error: any) {
            console.error(error);
            throw new Error("Erreur lors de récupération des configs.");
        };
    }

     /**
     * =====================================================
     * initSession
     * =====================================================
     *
     * Retrieves the authenticated user session.
     */
    async function initSession() : Promise<UserSessionModel> {
        try {
            const data = await AuthService.getCurrentSession();
            const userSession = data.data;
            return userSession;
        }
        catch (error: any) {
             console.error(error);
            throw new Error("Erreur lors de la récupération de la session utilisateur.");
        }
    };


    /**
     * =====================================================
     * checkSession
     * =====================================================
     *
     * Verifies whether a valid authenticated session exists.
     */
    async function checkSession() : Promise<{authenticated : boolean}> {
        try {
            const response = await AuthService.checkSession();
            return response.data;
        }
        catch (error: any) {
            console.error(error);
            throw new Error("Erreur lors de la vérification de la session utilisateur.");
        }
    };


    /**
     * =====================================================
     * createVisitor
     * =====================================================
     *
     * Generates a visitor fingerprint and retrieves
     * the associated challenge.
     */
    async function createVisitor() {
        try {
            const visitor = await getFingerprint();
            const { data } = await AuthService.getVisitorEnigma(visitor);
            return data;
        } catch (error: any) {
            console.error(error);
            throw new Error("Erreur lors de la récupération de l'énigme");
        }
    };

    /**
     * =====================================================
     * initTranslation
     * =====================================================
     *
     * Loads translations from cache or API depending on
     * online/offline mode.
     */
    async function initTranslation(isOnline: boolean) {
        try {
            let newTranslation = translation ? i18n.getI18nFromCache(translation, isOnline) : null;

            if (!newTranslation) {
                const lang = translation?.lang || DEFAULT_LANG;
                const translationResponse = await AppService.fetchI18n(lang, isOnline);
                const translationKeyPair = translationResponse.data as Record<string, string>;
                newTranslation = i18n.updateI18n(translationKeyPair, isOnline, lang);
            }
            return newTranslation;
        }
        catch (error: any) {
             console.error(error);
            throw new Error("Erreur lors de récupération des traductions.");
        };
    }

    return initResponse;
}